// Iter 72 — Vendor Services / Contracts (Phase 1.5)
// Tab on Vendor Detail listing the vendor's services with add/edit/status.
import { Fragment, useEffect, useState } from "react";
import {
    Plus, PencilSimple, Check, X, Clock, ArrowsClockwise,
    Repeat, Receipt, Warning, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    BILLING_FREQUENCY_LABEL, SERVICE_STATUS_LABEL, SERVICE_STATUS_BADGE,
    SERVICE_CATEGORY_LABEL, SERVICE_DEFAULT_ENUMS, formatAmount,
} from "./vendor_constants";
import VendorServiceInvoicesPanel from "./VendorServiceInvoicesPanel";

const BLANK_SERVICE = {
    name: "",
    description: "",
    service_category: "other",
    billing_frequency: "monthly",
    amount: 0,
    currency: "INR",
    start_date: "",
    end_date: "",
    next_due_date: "",
    auto_renewal: false,
    renewal_notice_days: 30,
    status: "active",
    notes: "",
};

export default function VendorServicesTab({ vendor }) {
    const { can } = useAuth();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);  // service object or null for create
    const [expandedId, setExpandedId] = useState(null);

    const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendors/${vendor.id}/services`)
            .then(({ data }) => {
                if (cancelled) return;
                setServices(data.items || []);
            })
            .catch((err) => {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load services"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [vendor.id, reloadKey]);

    const openCreate = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (svc) => { setEditing(svc); setDialogOpen(true); };

    const setStatus = async (svc, nextStatus) => {
        if (!window.confirm(`Change status of '${svc.name}' from '${SERVICE_STATUS_LABEL[svc.status]}' to '${SERVICE_STATUS_LABEL[nextStatus]}'?`)) return;
        try {
            await api.patch(`/vendor-services/${svc.id}/status`, { status: nextStatus });
            toast.success("Status updated");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Status change failed"));
        }
    };

    if (loading) {
        return <div className="text-sm text-slate-500 py-8 text-center" data-testid="vendor-services-loading">Loading services…</div>;
    }

    return (
        <div data-testid="vendor-services-tab">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-medium text-slate-900">Services & Contracts</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Operational source of truth: each service has its own billing cadence, dates, and renewal window.
                        Vendor-level Master Agreement dates are an informational umbrella above these.
                    </p>
                </div>
                {can("vendor:write") && (
                    <Button data-testid="vendor-service-add-btn" size="sm" onClick={openCreate} className="gap-1">
                        <Plus size={14} /> Add Service
                    </Button>
                )}
            </div>

            {services.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg" data-testid="vendor-services-empty">
                    <Repeat size={28} className="text-slate-300 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">No services yet</div>
                    {can("vendor:write") && (
                        <div className="text-xs text-slate-400 mt-1">Click &quot;Add Service&quot; to record the first one.</div>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="vendor-services-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="w-8" />
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Service</th>
                                <th className="text-left font-medium px-4 py-3">Category</th>
                                <th className="text-left font-medium px-4 py-3">Frequency</th>
                                <th className="text-right font-medium px-4 py-3">Amount</th>
                                <th className="text-left font-medium px-4 py-3">Next Due</th>
                                <th className="text-left font-medium px-4 py-3">End</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                {can("vendor:write") && <th className="w-10" />}
                            </tr>
                        </thead>
                        <tbody>
                            {services.map((s) => {
                                const badge = SERVICE_STATUS_BADGE[s.status] || SERVICE_STATUS_BADGE.cancelled;
                                const isExpanded = expandedId === s.id;
                                const colSpan = can("vendor:write") ? 10 : 9;
                                return (
                                    <Fragment key={s.id}>
                                        <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vendor-service-row-${s.code}`}>
                                            <td className="px-2 py-3">
                                                <button
                                                    data-testid={`vendor-service-expand-${s.code}`}
                                                    onClick={() => toggleExpand(s.id)}
                                                    aria-label={isExpanded ? "Collapse" : "Expand"}
                                                    className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100"
                                                >
                                                    {isExpanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.code}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{s.name}</div>
                                                {s.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{s.description}</div>}
                                                {s.auto_renewal && (
                                                    <Badge variant="secondary" className="bg-violet-50 text-violet-700 hover:bg-violet-50 text-[10px] mt-1 gap-1">
                                                        <ArrowsClockwise size={9} weight="bold" /> Auto-renew
                                                    </Badge>
                                                )}
                                                <ServiceInvoiceSummary
                                                    service={s}
                                                    onClickExpand={() => { if (!isExpanded) toggleExpand(s.id); }}
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 text-xs">
                                                {SERVICE_CATEGORY_LABEL[s.service_category] || s.service_category}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {BILLING_FREQUENCY_LABEL[s.billing_frequency] || s.billing_frequency}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-slate-900">
                                                {formatAmount(s.amount, s.currency)}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-700">
                                                {s.next_due_date ? formatDate(s.next_due_date) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-700">{s.end_date ? formatDate(s.end_date) : "—"}</td>
                                            <td className="px-4 py-3">
                                                <Badge data-testid={`vendor-service-status-${s.code}`} className={`${badge.className} hover:${badge.className}`}>
                                                    {badge.label}
                                                </Badge>
                                            </td>
                                            {can("vendor:write") && (
                                                <td className="px-4 py-3 text-right">
                                                    <RowActionsMenu service={s} onEdit={() => openEdit(s)} onStatus={setStatus} />
                                                </td>
                                            )}
                                        </tr>
                                        {isExpanded && (
                                            <tr data-testid={`vendor-service-expanded-${s.code}`} className="border-t border-slate-100">
                                                <td colSpan={colSpan} className="p-0">
                                                    <VendorServiceInvoicesPanel vendor={vendor} service={s} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ServiceDialog
                key={editing?.id || "create"}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={reload}
                vendor={vendor}
                editing={editing}
            />
        </div>
    );
}

function RowActionsMenu({ service, onEdit, onStatus }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                data-testid={`vendor-service-actions-${service.code}`}
                onClick={() => setOpen((o) => !o)}
                className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-slate-100 text-slate-500"
                aria-label="Actions"
            >
                ⋯
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg z-30 py-1">
                        <button
                            data-testid={`vendor-service-edit-${service.code}`}
                            onClick={() => { setOpen(false); onEdit(); }}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-2"
                        >
                            <PencilSimple size={12} /> Edit
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                        {["active", "paused", "pending_renewal", "expired", "cancelled"]
                            .filter((s) => s !== service.status)
                            .map((s) => (
                                <button
                                    key={s}
                                    data-testid={`vendor-service-set-${s}-${service.code}`}
                                    onClick={() => { setOpen(false); onStatus(service, s); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 capitalize text-slate-700"
                                >
                                    Mark {SERVICE_STATUS_LABEL[s]}
                                </button>
                            ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ServiceDialog({ open, onClose, onSaved, vendor, editing }) {
    const isEdit = !!editing;
    // The parent re-mounts this dialog (via the `key` prop) whenever the
    // editing target changes, so a useState initializer is sufficient —
    // no useEffect-based form sync needed (which would otherwise trip the
    // react-hooks/set-state-in-effect rule).
    const [form, setForm] = useState(() => {
        if (editing) {
            return {
                ...BLANK_SERVICE,
                ...Object.fromEntries(
                    Object.keys(BLANK_SERVICE).map((k) => [
                        k, editing[k] !== undefined && editing[k] !== null ? editing[k] : BLANK_SERVICE[k],
                    ])
                ),
            };
        }
        return { ...BLANK_SERVICE, currency: vendor.currency || "INR" };
    });
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
        setForm((f) => ({ ...f, [k]: v }));
    };
    const setNum = (k) => (e) => {
        const raw = e.target.value;
        setForm((f) => ({ ...f, [k]: raw === "" ? 0 : Number(raw) }));
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Service name is required"); return; }
        if (!form.start_date || !form.end_date) { toast.error("Start and end dates are required"); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            // Backend re-computes next_due_date when blank. Pass through whatever
            // operator entered (including blank). Don't coerce to empty string
            // on save — null/empty both treated as "auto-compute".
            if (isEdit) {
                await api.put(`/vendor-services/${editing.id}`, payload);
                toast.success("Service updated");
            } else {
                const r = await api.post(`/vendors/${vendor.id}/services`, payload);
                toast.success(`Service ${r.data.code} created`);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="vendor-service-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Service" : "Add Service"}</DialogTitle>
                    <DialogDescription>
                        {isEdit ? `Editing ${editing?.code}` : `For vendor ${vendor.name} (${vendor.code})`}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                    <FieldCell label="Service Name *" colSpan={2}>
                        <Input data-testid="svc-name" value={form.name} onChange={set("name")} maxLength={200} />
                    </FieldCell>
                    <FieldCell label="Service Category">
                        <Sel value={form.service_category} onChange={set("service_category")} testId="svc-category"
                            options={SERVICE_DEFAULT_ENUMS.service_categories.map((c) => [c, SERVICE_CATEGORY_LABEL[c]])} />
                    </FieldCell>
                    <FieldCell label="Billing Frequency *">
                        <Sel value={form.billing_frequency} onChange={set("billing_frequency")} testId="svc-frequency"
                            options={SERVICE_DEFAULT_ENUMS.billing_frequencies.map((c) => [c, BILLING_FREQUENCY_LABEL[c]])} />
                    </FieldCell>
                    <FieldCell label="Amount *">
                        <Input data-testid="svc-amount" type="number" min={0} value={form.amount} onChange={setNum("amount")} />
                    </FieldCell>
                    <FieldCell label="Currency">
                        <Sel value={form.currency} onChange={set("currency")} testId="svc-currency"
                            options={SERVICE_DEFAULT_ENUMS.currencies.map((c) => [c, c])} />
                    </FieldCell>
                    <FieldCell label="Start Date *">
                        <Input data-testid="svc-start" type="date" value={form.start_date} onChange={set("start_date")} />
                    </FieldCell>
                    <FieldCell label="End Date *">
                        <Input data-testid="svc-end" type="date" value={form.end_date} onChange={set("end_date")} />
                    </FieldCell>
                    <FieldCell label="Next Due Date" hint="Leave blank to auto-compute from start + frequency">
                        <Input data-testid="svc-next-due" type="date" value={form.next_due_date || ""} onChange={set("next_due_date")} />
                    </FieldCell>
                    <FieldCell label="Renewal Notice Days" hint="Used for future renewal alerts (default 30)">
                        <Input data-testid="svc-renewal-notice" type="number" min={0} max={365} value={form.renewal_notice_days} onChange={setNum("renewal_notice_days")} />
                    </FieldCell>
                    <FieldCell label="Auto Renewal">
                        <label className="inline-flex items-center gap-2 mt-2 text-sm">
                            <input type="checkbox" data-testid="svc-auto-renewal" checked={form.auto_renewal} onChange={set("auto_renewal")} className="h-4 w-4" />
                            <span className="text-slate-700">Service auto-renews on end date</span>
                        </label>
                    </FieldCell>
                    <FieldCell label="Status">
                        <Sel value={form.status} onChange={set("status")} testId="svc-status"
                            options={SERVICE_DEFAULT_ENUMS.statuses.map((s) => [s, SERVICE_STATUS_LABEL[s]])} />
                    </FieldCell>
                    <FieldCell label="Description" colSpan={2}>
                        <Input data-testid="svc-description" value={form.description} onChange={set("description")} maxLength={300} />
                    </FieldCell>
                    <FieldCell label="Notes" colSpan={2}>
                        <Textarea data-testid="svc-notes" rows={2} value={form.notes} onChange={set("notes")} />
                    </FieldCell>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="svc-save-btn" onClick={submit} disabled={busy}>
                        {busy ? "Saving…" : isEdit ? "Update" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldCell({ label, hint, children, colSpan }) {
    return (
        <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function Sel({ value, onChange, options, testId }) {
    return (
        <select
            data-testid={testId}
            value={value}
            onChange={onChange}
            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
        >
            {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
        </select>
    );
}

// Iter 78 — Compact per-service invoice summary chip strip. Renders
// only when at least one invoice exists. Click anywhere to expand the
// row and see the full Invoices table.
function ServiceInvoiceSummary({ service, onClickExpand }) {
    const sm = service.invoice_summary || null;
    if (!sm || (sm.total_count || 0) === 0) return null;
    const outstanding = Number(sm.unpaid_total || 0);
    const paid = Number(sm.paid_total || 0);
    return (
        <button
            type="button"
            onClick={onClickExpand}
            data-testid={`vendor-service-invoice-summary-${service.code}`}
            className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-left text-slate-600 rounded hover:bg-slate-50/80 px-1 -mx-1"
            title="Click to view invoices"
        >
            <span className="inline-flex items-center gap-1" data-testid={`vsi-summary-total-${service.code}`}>
                <span className="text-slate-400">Invoices:</span>
                <span className="font-medium text-slate-700">{sm.total_count}</span>
            </span>
            <span className="text-slate-200">•</span>
            <span className="inline-flex items-center gap-1" data-testid={`vsi-summary-paid-${service.code}`}>
                <span className="text-slate-400">Paid:</span>
                <span className="font-medium text-emerald-700 font-mono">{formatAmount(paid, service.currency)}</span>
            </span>
            <span className="text-slate-200">•</span>
            <span className="inline-flex items-center gap-1" data-testid={`vsi-summary-outstanding-${service.code}`}>
                <span className="text-slate-400">Outstanding:</span>
                <span className={`font-medium font-mono ${outstanding > 0 ? "text-rose-700" : "text-slate-500"}`}>
                    {formatAmount(outstanding, service.currency)}
                </span>
            </span>
            {sm.unpaid_count > 0 && (
                <>
                    <span className="text-slate-200">•</span>
                    <span className="inline-flex items-center gap-1" data-testid={`vsi-summary-unpaid-count-${service.code}`}>
                        <span className="text-slate-400">Unpaid:</span>
                        <span className="font-medium text-amber-700">{sm.unpaid_count}</span>
                    </span>
                </>
            )}
        </button>
    );
}

